import React, { useState, useRef } from 'react';
import { apiService } from '../../services/api';
import { Anexo } from '../../types';
import './FileUpload.css';

interface FileUploadProps {
  leadId: string;
  onUploadComplete: (anexo: Anexo) => void;
  onError?: (error: string) => void;
  maxSizeMB?: number;
  allowedTypes?: string[];
}

const DEFAULT_ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'audio/ogg',
  'audio/mpeg',
  'audio/mp4',
  'audio/webm',
  'audio/wav',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];

const FILE_TYPE_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'Imagem',
  'image/png': 'Imagem',
  'image/gif': 'Imagem',
  'image/webp': 'Imagem',
  'audio/ogg': 'Áudio',
  'audio/mpeg': 'Áudio',
  'audio/mp4': 'Áudio',
  'audio/webm': 'Áudio',
  'audio/wav': 'Áudio',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'text/plain': 'Texto',
};

export const FileUpload: React.FC<FileUploadProps> = ({
  leadId,
  onUploadComplete,
  onError,
  maxSizeMB = 10,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      onError?.(`Tipo de arquivo não permitido: ${file.type}`);
      return;
    }

    // Validate file size
    if (file.size > maxSizeBytes) {
      onError?.(`Arquivo muito grande. Máximo: ${maxSizeMB}MB`);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setProgress(0);

    try {
      // Step 1: Get pre-signed upload URL
      setProgress(10);
      const { uploadUrl, s3Key, anexoId, mimeType } = await apiService.getUploadUrl(
        leadId,
        selectedFile.name,
        selectedFile.type
      );

      // Step 2: Upload file directly to S3
      setProgress(30);
      await uploadToS3(uploadUrl, selectedFile, (pct) => {
        setProgress(30 + Math.round(pct * 0.5)); // 30-80%
      });

      // Step 3: Confirm upload
      setProgress(85);
      const { anexo } = await apiService.confirmUpload(leadId, anexoId, {
        s3Key,
        fileName: selectedFile.name,
        mimeType,
        fileSize: selectedFile.size,
      });

      setProgress(100);
      onUploadComplete(anexo);
      setSelectedFile(null);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      onError?.(error.message || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const uploadToS3 = (url: string, file: File, onProgress: (pct: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded / event.total);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  };

  const handleCancel = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileTypeLabel = (type: string): string => {
    return FILE_TYPE_LABELS[type] || 'Arquivo';
  };

  return (
    <div className="file-upload">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept={allowedTypes.join(',')}
        disabled={uploading}
        className="file-input"
        id="file-upload-input"
      />

      {!selectedFile && (
        <label htmlFor="file-upload-input" className="file-upload-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
        </label>
      )}

      {selectedFile && (
        <div className="file-preview">
          <div className="file-info">
            <span className="file-type">{getFileTypeLabel(selectedFile.type)}</span>
            <span className="file-name">{selectedFile.name}</span>
            <span className="file-size">{formatFileSize(selectedFile.size)}</span>
          </div>

          {uploading ? (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="progress-text">{progress}%</span>
            </div>
          ) : (
            <div className="file-actions">
              <button
                className="upload-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                Enviar
              </button>
              <button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
